import { ChangeDetectionStrategy, Component, ElementRef, Inject, Renderer2, signal, type OnInit, ViewChild, type OnDestroy } from '@angular/core'
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog'
import { NGXLogger } from 'ngx-logger'
import { SharedService } from '../../services/shared.service'
import { TranslationService } from '@emilfreydigital/transifex-angular'
import { SnackbarService } from '../../services/snackbar.service'
import { DOCUMENT, NgClass } from '@angular/common'
import { MatIcon } from '@angular/material/icon'
import { DOMSafeUrlPipe } from '../../pipes/dom-safe-url.pipe'
import { EfSpinnerComponent } from '../revolution/ef-spinner/ef-spinner.component'


export interface PagePreviewDialogData {
  title: string
  url: string
  iframe: Iframe
}

export enum Iframe {
  ServiceManager = 'serviceManager',
  ConsentManagement = 'consentManagement',
}

export enum ConsentManagementIframeMessageEnum {
  COMPLETED = 'spaCompletedCallback',
  SKIPPED = 'spaSkippedCallback',
  ERROR = 'spaErrorCallback'
}

export interface PagePreviewDialogResponse {
  reloadData: boolean
}

@Component({
  selector: 'customer-portal-page-preview-dialog',
  templateUrl: './page-preview-dialog.component.html',
  imports: [
    MatDialogContent,
    NgClass,
    DOMSafeUrlPipe,
    MatDialogClose,
    MatIcon,
    MatDialogTitle,
    EfSpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PagePreviewDialogComponent implements OnInit, OnDestroy {
  Iframe = Iframe
  trustedOrigin: string
  readonly loading = signal(true)
  private readonly messageHandler = this.onMessage.bind(this)
  private preconnectLink?: HTMLLinkElement
  @ViewChild('iframeRef', { static: false })
    iframeRef?: ElementRef<HTMLIFrameElement>

  constructor (
    @Inject(MAT_DIALOG_DATA) public dialogData: PagePreviewDialogData,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly dialog: MatDialogRef<
    PagePreviewDialogComponent,
    PagePreviewDialogResponse
    >,
    private readonly renderer: Renderer2,
    public readonly sharedService: SharedService,
    private readonly snackbarService: SnackbarService,
    private readonly transifexTranslationsService: TranslationService,
    private readonly logger: NGXLogger
  ) {}

  ngOnInit (): void {
    const url = this.dialogData?.url
    if (url) {
      this.trustedOrigin = new URL(url).origin
      this.addPreconnect(this.trustedOrigin)
      this.addListener()
    } else {
      this.logger.warn('Missing url')
    }
  }

  ngOnDestroy (): void {
    this.removeListener()
    if (this.preconnectLink) {
      this.renderer.removeChild(this.document.head, this.preconnectLink)
    }
  }

  onIframeLoad (): void {
    this.loading.set(false)
  }

  // warms up DNS/TCP/TLS for the iframe origin while the dialog is still opening
  private addPreconnect (origin: string): void {
    const link: HTMLLinkElement = this.renderer.createElement('link')
    this.renderer.setAttribute(link, 'rel', 'preconnect')
    this.renderer.setAttribute(link, 'href', origin)
    this.renderer.setAttribute(link, 'crossorigin', '')
    this.renderer.appendChild(this.document.head, link)
    this.preconnectLink = link
  }

  private addListener (): void {
    window.addEventListener('message', this.messageHandler)
  }

  private removeListener (): void {
    window.removeEventListener('message', this.messageHandler)
  }

  private onMessage (event: MessageEvent): void {
    if (event.origin !== this.trustedOrigin) {
      this.logger.warn('Untrusted origin:', event.origin)
      return
    }

    const data = event.data
    this.handleMessage(data)
    this.logger.debug('Received message:', data)
  }

  private handleMessage (message: string): void {
    switch (this.dialogData.iframe) {
      case Iframe.ServiceManager:
        break
      case Iframe.ConsentManagement:
        this.handleConsentManagementMessage(message)
        break
      default:
        this.logger.warn('Unknown iframe type:', this.dialogData.iframe)
    }
  }

  handleConsentManagementMessage (message: string): void {
    const heightMatch = message.match(/setIframeHeight\s*\(\s*(\d+)\s*\)/i)

    if (heightMatch) {
      const height = parseInt(heightMatch[1], 10)
      this.setIframeHeight(height)
    }

    if (message.includes(ConsentManagementIframeMessageEnum.COMPLETED)) {
      this.closeDialog(true)
    }

    if (message.includes(ConsentManagementIframeMessageEnum.SKIPPED)) {
      this.closeDialog(false)
    }

    if (message.includes(ConsentManagementIframeMessageEnum.ERROR)) {
      this.showConsentManagementError()
    }
  }

  private setIframeHeight (height: number): void {
    if (this.iframeRef?.nativeElement) {
      this.iframeRef.nativeElement.style.setProperty(
        'height',
        `${height}px`,
        'important'
      )
    } else {
      this.logger.warn('iframeRef not available')
    }
  }

  private showConsentManagementError (): void {
    const mssg = this.transifexTranslationsService.translate(
      'Error occured when saving consents!',
      {
        _key: 'customerPortal.customer-portal.gdpr.consent-management.error',
        _tags: 'customer-portal, 3.32'
      }
    )
    this.snackbarService.showSnackbar(null, mssg, 0)
  }

  private closeDialog (reloadData: boolean): void {
    this.dialog.close({ reloadData })
  }
}
